import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

// Import Zustand stores
import { useStudioStore } from "store/zustand-store/Studio/StudioStore";
import { useEditorStore } from "store/zustand-store/Studio/EditorStore";
import { useDrawingStore } from "store/zustand-store/Studio/DrawingStore";
import { useMoveableStore } from "store/zustand-store/Studio/MoveableStore";
import { useTextFieldStore } from "store/zustand-store/Studio/TextFieldStore";
import { useCanvasStore } from "store/zustand-store/Studio/CanvasStore";
import { useStudioEditStore } from "store/zustand-store/Studio/useStudioEditStore ";
import { clearNavigationHistory } from "./useNavigationHistory";
import { clearAuthCookies } from "utils/cookieService";
import { clearOfflineAccessConnections } from "utils/offlineAccessStorage";
import { useClearSessionAndLogout } from "hooks/query/useAccount";

/**
 * Custom hook that provides a logout function which clears all application state
 * including localStorage, Redux stores, and Zustand stores
 */
export const useLogout = (shouldRedirect = true) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mutateAsync: clearSessionAndLogout } = useClearSessionAndLogout();

  const clearLocalSession = () => {
    // Clear auth cookies (for cross-subdomain auth)
    clearAuthCookies();

    // Clear localStorage
    localStorage.clear();

    // Clear stored offline-access connections (explicit, in case the blanket
    // localStorage.clear() above is ever narrowed).
    clearOfflineAccessConnections();

    // Clear navigation history
    clearNavigationHistory();

    // Reset all Redux stores to initial state
    dispatch({ type: "RESET_ALL" });

    // Clear all Zustand stores - reset to initial state
    useStudioStore.setState({
      presentationDisplayDetailsRef: null,
    });

    useEditorStore.setState({
      editor: null,
      activeAttributes: {},
    });

    useDrawingStore.setState({
      selectedShapeId: null,
      drawingState: {
        isDrawing: false,
        currentShape: [],
        shapeType: "line",
      },
      toolMode: "select",
      currentTool: "line",
      lineColor: "#000000",
      fillColor: "#ffffff",
      lineWidth: 2,
      startEnd: "none",
      endEnd: "none",
      showFill: false,
      borderRadius: 0,
      snapPoints: [],
      isSnapping: true,
      snapDistance: 15,
      starSpikes: 5,
      starInnerRadius: 0.4,
      pieStartAngle: 0,
      pieEndAngle: 90,
      dragState: {
        isDragging: false,
        shapeId: null,
        pointIndex: null,
        offset: { x: 0, y: 0 },
      },
      isHoveringEndpoint: false,
      currentMousePos: null,
    });

    useMoveableStore.setState({
      moveableRef: null,
      targets: [],
      renderDirections: ["n", "s", "e", "w", "nw", "ne", "sw", "se"],
      rotatable: true,
    });

    useTextFieldStore.setState({
      text: "Edit me",
      fontSize: 20,
      fontStyle: "normal",
      fontWeight: "normal",
      fill: "#000000",
      textAlign: "left",
      position: { x: 100, y: 100 },
    });

    useCanvasStore.setState({
      blocks: [],
      activeBlockId: null,
      clipboard: null,
      canvasBackgroundColor: "#FEFFF9",
      canvasWidth: 800,
      canvasHeight: 450,
      isDragging: false,
      focusedBlockId: null,
      canvasScale: 1,
    });

    useStudioEditStore.getState().clearAll();
    useStudioEditStore.setState({
      docsLoaded: {},
      activeDocumentId: "",
    });

    // Navigate to login page
    if (shouldRedirect) {
      navigate("/login");
    }
  };

  const logout = async () => {
    try {
      await clearSessionAndLogout();
    } catch (error) {
      console.error("Failed to clear backend session during logout", error);
    } finally {
      clearLocalSession();
    }
  };

  return logout;
};
